import React, { useEffect, useState, useContext, useRef } from 'react';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaCalendarAlt, FaTimes } from "react-icons/fa";
import './TableLayout.css';
import { ThemeContext } from "../../Shared/Themes/ThemeContext"; 
import { FontSizeContext } from '../../Shared/Font/FontContext';

// Global Search Filter Component
function GlobalFilter({ globalFilter, setGlobalFilter, handleDateFilter }) {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);

  const applyDateFilter = () => {
    handleDateFilter(fromDate, toDate);
  };

  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
    handleDateFilter('', '');
    setShowDateFilters(false);
  };

  return (
    <div className="dataTable_search mb-3 d-flex align-items-center gap-2">
      <input
        value={globalFilter || ''}
        onChange={(e) => { 
          setGlobalFilter(e.target.value);
          const url = new URL(window.location);
          url.searchParams.delete('page');
          window.history.pushState({}, '', url);
        }}
        className="form-control search-input"
        placeholder="Search..."
      />
      {showDateFilters || fromDate || toDate ? (
        <button className="btn btn-light clear-btn" onClick={clearDateFilter}>
          <FaTimes color="#ff5e62" size={20} />
        </button>
      ) : (
        <button className="btn btn-light calendar-btn" onClick={() => setShowDateFilters(!showDateFilters)}>
          <FaCalendarAlt color="#ff5e62" size={20} />
        </button>
      )}
      {showDateFilters && (
        <div className="date-filters d-flex gap-2 align-items-center">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              if (toDate && e.target.value > toDate) {
                setToDate('');
              }
            }}
            className="form-control date-input"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="form-control date-input"
            min={fromDate}
          />
          <button
            onClick={() => {
              applyDateFilter();
              const url = new URL(window.location);
              url.searchParams.delete("page");
              window.history.pushState({}, "", url);
            }}
            className="btn btn-primary apply-btn"
          >
            OK
          </button>
        </div>
      )}
    </div>
  );
}

export default function DataTable({ 
  columns, 
  data, 
  pageIndex: controlledPageIndex, 
  setPageIndex,
  pageSize: controlledPageSize,
  setPageSize: setControlledPageSize
}) {
  const [filteredData, setFilteredData] = useState(data);
  const [searchInput, setSearchInput] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(controlledPageIndex || 0);
  const [isFiltered, setIsFiltered] = useState(false);
  const { themeColor } = useContext(ThemeContext);
  const { fontSize, setFontSize } = useContext(FontSizeContext);

  // ✅ Ref to track who triggered the page change and break the loop
  const isInternalChange = useRef(false);

  const getInitialPageSize = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageSizeParam = urlParams.get('pageSize');
    return pageSizeParam ? parseInt(pageSizeParam) : 20;
  };
  
  const [pageSize, setPageSizeState] = useState(getInitialPageSize);

  useEffect(() => {
    applyGlobalSearch(searchInput);
  }, [searchInput, data]);

  const applyGlobalSearch = (searchValue) => {
    if (!searchValue) {
      setFilteredData(data);
      setIsFiltered(false);
      return;
    }
    const filtered = data.filter((item) => {
      return Object.values(item)
        .join(' ')
        .toLowerCase()
        .includes(searchValue.toLowerCase());
    });
    setFilteredData(filtered);
    setIsFiltered(true);
  };

  const handleDateFilter = (fromDate, toDate) => {
    if (fromDate || toDate) {
      const filtered = data.filter((item) => {
        const itemDate = new Date(item.updated_at).setHours(0, 0, 0, 0);
        const from = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
        const to = toDate ? new Date(toDate).setHours(0, 0, 0, 0) : null;
        return (!from || itemDate >= from) && (!to || itemDate <= to);
      });
      setFilteredData(filtered);
      setIsFiltered(true);
    } else {
      setFilteredData(data);
      setIsFiltered(false);
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex },
    gotoPage,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { 
        pageIndex: currentPageIndex, 
        pageSize: pageSize 
      },
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageSizeParam = urlParams.get('pageSize');
    if (!pageSizeParam) {
      setPageSizeState(20);
      if (setPageSize) {
        setPageSize(20);
      }
    }
  }, [window.location.search, setPageSize]);

  // ✅ FIXED: External → internal sync.
  // When parent sets controlledPageIndex (e.g. from URL on load),
  // call gotoPage() so react-table actually moves. 
  // isInternalChange = false means "parent drove this, don't echo back"
  useEffect(() => {
    if (controlledPageIndex !== undefined && controlledPageIndex !== pageIndex) {
      isInternalChange.current = false;
      gotoPage(controlledPageIndex);
    }
  }, [controlledPageIndex]);

  useEffect(() => {
    if (isFiltered) {
      gotoPage(0);
      setIsFiltered(false);
    }
  }, [filteredData, isFiltered, gotoPage]);

  // ✅ FIXED: Internal → external sync.
  // Only tells parent when isInternalChange = true (user clicked Prev/Next).
  // When parent drove the change, isInternalChange = false so we skip setPageIndex,
  // which breaks the ping-pong loop that caused shaking.
  useEffect(() => {
    if (isInternalChange.current && setPageIndex) {
      setPageIndex(pageIndex);
    }
    isInternalChange.current = false;
  }, [pageIndex]);

  const handlePageChange = (newPageIndex) => {
    isInternalChange.current = true; // ✅ User drove this change
    gotoPage(newPageIndex);
    if (setPageIndex) setPageIndex(newPageIndex);
    const url = new URL(window.location);
    url.searchParams.set('page', newPageIndex + 1);
    if (pageSize !== 20) {
      url.searchParams.set('pageSize', pageSize);
    }
    window.history.pushState({}, '', url);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSizeState(newSize);
    setPageSize(newSize);
    gotoPage(0);
    const url = new URL(window.location);
    url.searchParams.set('page', '1');
    if (newSize !== 20) {
      url.searchParams.set('pageSize', newSize);
    } else {
      url.searchParams.delete('pageSize');
    }
    window.history.pushState({}, '', url);
  };

  return (
    <div className="dataTable_wrapper container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="col-auto d-flex align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {[20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              value={fontSize}
              onChange={(e) => setFontSize(e.target.value)}
            >
              <option value="12px">Small</option>
              <option value="14px">Medium</option>
              <option value="16px">Default</option>
              <option value="18px">Large</option>
              <option value="20px">Extra Large</option>
            </select>
          </div>
          <span className="fw-bold">Total Records: {filteredData.length}</span>
        </div>
        <GlobalFilter 
          globalFilter={searchInput} 
          setGlobalFilter={setSearchInput} 
          handleDateFilter={handleDateFilter} 
        />
      </div>

      <div className="table-responsive">
        <table {...getTableProps()} className="table table-striped">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="dataTable_headerRow">
                {headerGroup.headers.map((column) => (
                  <th 
                    {...column.getHeaderProps(column.getSortByToggleProps())} 
                    className="dataTable_headerCell"
                    style={{
                      backgroundColor: themeColor,
                      color: 'white',
                      border: '2px solid',
                      borderImage: 'linear-gradient(to right, #ff9966, #ff5e62) 1',
                      textAlign: 'center',
                      fontSize: fontSize,
                    }}
                  >
                    {column.render('Header')}
                    <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="dataTable_body">
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="dataTable_row">
                  {row.cells.map((cell) => (
                    <td 
                      {...cell.getCellProps()} 
                      className="dataTable_cell"
                      style={{
                        borderTop: `2px solid ${themeColor}`,
                        borderBottom: `2px solid ${themeColor}`,
                        borderLeft: `2px solid ${themeColor}`,
                        borderRight: `2px solid ${themeColor}`,
                        borderImage: `linear-gradient(to right, ${themeColor}, #ff5e62) 1`,
                        fontSize: fontSize,
                      }}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center justify-content-between mt-3">
        <div className="dataTable_pageInfo">
          Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
        </div>
        <div className="pagebuttons">
          <button
            className="btn btn-primary me-2 btn1"
            onClick={() => handlePageChange(pageIndex - 1)}
            disabled={!canPreviousPage}
          >
            Prev
          </button>
          <button
            className="btn btn-primary btn1"
            onClick={() => handlePageChange(pageIndex + 1)}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}